import { useEffect, useReducer, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';

import { ACTIONS, clientReducer } from './clientReducer';
import { initialUpdateClientState } from './clientInitialStates';
import ClientForm from './clientForm';
import DuplicateWarningModal from './DuplicateWarningModal';
import { errorNormalizer } from '../../../shared/Helpers/functions';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { getClient, updateClient } from '../../../redux/State/clientSlice/clientSlice';
import { getUserBranches } from '../../../redux/State/userSlice';
import {
	DuplicateMatchProperties,
	UpdateClientInfoProperties,
} from '../../../redux/API/ClientAPIHelpers/clientProperties';

const UpdateClient = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const navigate = useNavigate();
	const { clientId } = useParams();

	const currentUserId = useSelector((state) => state.account.user.id);
	const userBranches = useSelector((state) => state.userBranch.userBranches);

	const [clientInfo, setClientInfo] = useReducer(clientReducer, initialUpdateClientState);
	const [duplicates, setDuplicates] = useState<DuplicateMatchProperties[]>([]);
	const [showDuplicateWarning, setShowDuplicateWarning] = useState<boolean>(false);
	const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

	useEffect(() => {
		if (!currentUserId) return;
		dispatch(getUserBranches(currentUserId));
	}, [currentUserId, dispatch]);

	useEffect(() => {
		if (!clientId) return;
		dispatch(getClient(clientId))
			.then(unwrapResult)
			.then((response) => {
				const { status, diagnosisCode, caseCoordinator, branches, insurances } = response;
				const diagnosisCodeId = diagnosisCode?.id || '';
				const caseCoordinatorId = caseCoordinator?.id || '';
				// Extract branchId only if it's explicitly assigned (not auto-assigned)
				const branchId = Boolean(branches?.length) && !branches[0].isAutoAssigned ? branches[0].id : '';
				const payload: UpdateClientInfoProperties = {
					...response,
					status: status.id,
					diagnosisCodeId,
					caseCoordinatorId,
					branchId,
					insurances: insurances || [], // Explicitly preserve insurances
				};

				setClientInfo({ type: ACTIONS.setState, payload });
			})
			.catch(errorNormalizer);
	}, [clientId, dispatch]);

	const getClientData = () => ({
		...clientInfo,
		firstName: clientInfo.firstName.trim(),
		lastName: clientInfo.lastName.trim(),
	});

	const navigateAfterUpdate = (response: any) => {
		const updatedClientBranches = response?.branches || [];
		const hasAccess =
			!updatedClientBranches.length ||
			updatedClientBranches.some((clientBranch) =>
				userBranches.some((userBranch) => userBranch.id === clientBranch.id),
			);

		if (hasAccess) {
			navigate(-1);
		} else {
			navigate('/clients');
		}
	};

	const updateClientHandler = () => {
		dispatch(updateClient(getClientData()))
			.then(unwrapResult)
			.then(navigateAfterUpdate)
			.catch((error) => {
				if (error?.duplicates) {
					setDuplicates(error.duplicates);
					setShowDuplicateWarning(true);

					return;
				}

				if (error?.errors) {
					setServerErrors(error.errors);
				}
				errorNormalizer(error);
			});
	};

	const onConfirmDuplicate = () => {
		setShowDuplicateWarning(false);
		dispatch(updateClient({ ...getClientData(), acknowledgeDuplicates: true }))
			.then(unwrapResult)
			.then(navigateAfterUpdate)
			.catch(errorNormalizer);
	};

	const onCancelDuplicate = () => {
		setShowDuplicateWarning(false);
		setDuplicates([]);
	};

	return (
		<>
			<ClientForm
				clientInfo={clientInfo}
				setClientInfo={setClientInfo}
				saveClientHandler={updateClientHandler}
				duplicateWarnings={duplicates}
				serverErrors={serverErrors}
			/>
			<DuplicateWarningModal
				open={showDuplicateWarning}
				duplicates={duplicates}
				onConfirm={onConfirmDuplicate}
				onCancel={onCancelDuplicate}
			/>
		</>
	);
};

export default UpdateClient;
